/* eslint-disable @typescript-eslint/naming-convention */
import { useEffect, useRef, useState } from 'react';

import type { UseMutateAsyncFunction, UseMutateFunction } from '@tanstack/react-query';
import { useMutation, useQuery } from '@tanstack/react-query';

import { JobCreateError, JobFailedError, TimeoutError } from './errors';

type JobStatus = 'IN_PROGRESS' | 'COMPLETED' | 'ERROR' | 'CANCELLED';
type JobResponse = {
	status: JobStatus | Lowercase<JobStatus>;
};

type UseAsyncJobResult<TData extends JobResponse, TJobId, TVariables> =
	| Readonly<{
			status: 'idle';
			data: null;
			error: null;
			mutate: UseMutateFunction<{ jobId: TJobId }, Error, TVariables>;
			mutateAsync: UseMutateAsyncFunction<{ jobId: TJobId }, Error, TVariables>;
	  }>
	| Readonly<{
			status: 'pending';
			data: null;
			error: null;
			mutate: UseMutateFunction<{ jobId: TJobId }, Error, TVariables>;
			mutateAsync: UseMutateAsyncFunction<{ jobId: TJobId }, Error, TVariables>;
	  }>
	| Readonly<{
			status: 'success';
			data: TData;
			error: null;
			mutate: UseMutateFunction<{ jobId: TJobId }, Error, TVariables>;
			mutateAsync: UseMutateAsyncFunction<{ jobId: TJobId }, Error, TVariables>;
	  }>
	| Readonly<{
			status: 'error';
			data: null;
			error: Error;
			mutate: UseMutateFunction<{ jobId: TJobId }, Error, TVariables>;
			mutateAsync: UseMutateAsyncFunction<{ jobId: TJobId }, Error, TVariables>;
	  }>;

type UseAsyncJobOptions<TData extends JobResponse, TVariables, TJobId> = {
	mutationFn: (variables: TVariables) => Promise<{ jobId: TJobId }>;
	queryFn: (jobId: TJobId) => Promise<TData>;
	jobId?: TJobId | null;
	enabled?: boolean;
	timeout?: number;
	refetchInterval?: number;
	onSuccess?: (data: TData) => void;
	onError?: (error: Error) => void;
};

// oxlint-disable-next-line complexity
function getAsyncJobResult<TData extends JobResponse, TVariables, TJobId>(
	mutation: ReturnType<typeof useMutation<{ jobId: TJobId }, Error, TVariables>>,
	query: ReturnType<typeof useQuery<TData>>,
	timeout: boolean,
): UseAsyncJobResult<TData, TJobId, TVariables> {
	const base = { mutate: mutation.mutate, mutateAsync: mutation.mutateAsync, data: null, error: null } as const;

	if (mutation.isPending) {
		return { ...base, status: 'pending' };
	}

	if (query.isFetching || query.isLoading) {
		return { ...base, status: 'pending' };
	}

	if (mutation.isError) {
		return { ...base, status: 'error', error: new JobCreateError('Job creation failed') };
	}

	if (timeout) {
		return { ...base, status: 'error', error: new TimeoutError('Job timed out') };
	}

	if (query.data?.status.toLowerCase() === 'error') {
		return { ...base, status: 'error', error: new JobFailedError('Job failed') };
	}

	if (query.data?.status.toLowerCase() === 'completed') {
		return { ...base, status: 'success', data: query.data };
	}

	if (mutation.isIdle && !query.data) {
		return { ...base, status: 'idle' };
	}

	return { ...base, status: 'pending' };
}

/**
 * A React hook for managing asynchronous jobs with polling and timeout capabilities.
 *
 * This hook combines a mutation (to create/start a job) with polling (to check job status)
 * and provides a unified interface for handling the entire async job lifecycle.
 *
 * **Important**: The `onSuccess` and `onError` callbacks fire when the **job completes**,
 * not when the mutation succeeds. To handle mutation-specific callbacks, pass them to `mutate()`.
 *
 * See `/apps/front-end/src/app/shared/hooks/asyncJob/readme.md` for detailed documentation,
 * usage patterns, and API requirements.
 *
 * @template TData - The shape of the job response data (must include a `status` field)
 * @template TVariables - The variables passed to the mutation function to start the job
 * @template TJobId - The type of the job identifier (defaults to `string`)
 *
 * @param options - Configuration options for the async job
 * @param options.mutationFn - Function that creates/starts the job and returns a job ID
 * @param options.queryFn - Function that fetches the current job status given a job ID
 * @param options.jobId - Optional initial job ID to start polling immediately
 * @param options.enabled - Whether polling is enabled (defaults to `true`)
 * @param options.timeout - Maximum time in milliseconds to wait for job completion (defaults to `15000`)
 * @param options.refetchInterval - Polling interval in milliseconds (defaults to `1000`)
 * @param options.onSuccess - Callback invoked when the job completes successfully
 * @param options.onError - Callback invoked when the job fails or times out
 *
 * @returns An object containing:
 * - `status`: Current state of the job (`'idle'` | `'pending'` | `'success'` | `'error'`)
 * - `data`: Job data when successful, otherwise `null`
 * - `error`: Error object when failed, otherwise `null`
 * - `mutate`: Function to trigger the job mutation
 * - `mutateAsync`: Async function to trigger the job mutation
 *
 * @example
 * ```typescript
 * // Single-page usage: create and monitor job
 * const uploadJob = useAsyncJob({
 *   mutationFn: async (file: File) => {
 *     const response = await uploadFile(file);
 *     return { jobId: response.jobId };
 *   },
 *   queryFn: async (jobId: string) => {
 *     return await getJobStatus(jobId);
 *   },
 *   timeout: 30000,
 *   onSuccess: (data) => {
 *     console.log('Upload completed:', data);
 *   },
 * });
 *
 * uploadJob.mutate(myFile, {
 *   onSuccess: (response) => console.log('Job created:', response.jobId),
 * });
 * ```
 *
 * @example
 * ```typescript
 * // Multi-page usage: resume polling with existing job ID
 * const monitorJob = useAsyncJob({
 *   mutationFn: () => Promise.resolve({ jobId: '' }), // Not used
 *   queryFn: getJobStatus,
 *   jobId: existingJobId, // Start polling immediately
 *   onSuccess: (data) => {
 *     showNotification('Job complete!');
 *   },
 * });
 * ```
 *
 * @example
 * ```typescript
 * // Create job without polling: useful for storing job ID and navigating away
 * const createJob = useAsyncJob({
 *   mutationFn: async (file: File) => {
 *     const response = await uploadFile(file);
 *     return { jobId: response.jobId };
 *   },
 *   queryFn: getJobStatus,
 *   enabled: false, // Don't start polling
 * });
 *
 * createJob.mutate(myFile, {
 *   onSuccess: (response) => {
 *     saveJobId(response.jobId);
 *     navigate('/job-status');
 *   },
 * });
 * ```
 */
export function useAsyncJob<TData extends JobResponse = JobResponse, TVariables = void, TJobId = string>({
	mutationFn,
	queryFn,
	jobId,
	enabled = true,
	timeout = 15000,
	refetchInterval = 1000,
	onSuccess,
	onError,
}: UseAsyncJobOptions<TData, TVariables, TJobId>): ReturnType<typeof getAsyncJobResult<TData, TVariables, TJobId>> {
	const [jobIdRef, setJobIdRef] = useState<TJobId | null>(jobId ?? null);
	const completedJobsRef = useRef(new Set<TJobId>());
	const jobStartTimesRef = useRef(new Map<TJobId, number>());

	const mutation = useMutation({
		mutationFn,
		onSuccess: (response: { jobId: TJobId }) => {
			setJobIdRef(response.jobId);
		},
	});

	// Wrap queryFn to track job start times
	const wrappedQueryFn = async (currentJobId: TJobId): Promise<TData> => {
		if (!jobStartTimesRef.current.has(currentJobId)) {
			const startTime = mutation.submittedAt === 0 ? Date.now() : mutation.submittedAt;
			jobStartTimesRef.current.set(currentJobId, startTime);
		}
		return queryFn(currentJobId);
	};

	// eslint-disable-next-line react-hooks/refs
	const startTime = jobIdRef ? jobStartTimesRef.current.get(jobIdRef) : null;
	// eslint-disable-next-line react-hooks/purity
	const jobTimeout = startTime ? Date.now() - startTime > timeout : false;

	const query = useQuery<TData>({
		queryKey: ['async-job', jobIdRef],

		// oxlint-disable-next-line typescript/no-non-null-assertion
		queryFn: async () => wrappedQueryFn(jobIdRef!),
		enabled: !!jobIdRef && enabled,
		refetchInterval: (q) => {
			if (jobTimeout) return false;
			if (q.state.data?.status.toLowerCase() === 'completed') return false;
			if (q.state.data?.status.toLowerCase() === 'error') return false;
			if (q.state.data?.status.toLowerCase() === 'cancelled') return false;
			return refetchInterval;
		},
	});

	// Get current job result
	const jobResult = getAsyncJobResult(mutation, query, jobTimeout);

	// Handle job-level callbacks
	useEffect(() => {
		const currentJobId = jobIdRef;
		if (!currentJobId || completedJobsRef.current.has(currentJobId)) return;

		if (jobResult.status === 'success') {
			completedJobsRef.current.add(currentJobId);
			onSuccess?.(jobResult.data);
		}
	}, [jobResult.status, query.data, onSuccess, jobIdRef, jobResult.data]);

	useEffect(() => {
		const currentJobId = jobIdRef;
		if (!currentJobId || completedJobsRef.current.has(currentJobId)) return;

		if (jobResult.status === 'error') {
			completedJobsRef.current.add(currentJobId);
			onError?.(jobResult.error);
		}
	}, [jobResult.status, jobResult.error, query.data, onError, jobIdRef]);

	return jobResult;
}
